import React, {useState} from "react";
import { Modal, Button, Row, Col, Badge, Spinner } from "react-bootstrap";
import { formatDate } from "../../utils/helpers";
import {
  KeyIcon,
  UserIconModal,
  CalendarIcon,
  CompanyIcon,
  ServerIcon,
  Elipses,
  EmailIcon,
  LocationIcon,
  CheckCircleIcon,
  TimesCircleIcon,
  AdvancedIcon,
  SelfServiceIcon,
} from "../Icons";
import { useAuth } from "../../hooks/useAuth";
import { toast } from "react-toastify";


const LicenseDetailsModal = ({ license, show, onHide, refetch }) => {
  const [newLicense, setLicense] = useState(license)
  const [isLoading, setIsLoading] = useState(false)
  const {backendUrl, token} = useAuth()
  if (!newLicense) return null;

  const partner = newLicense.partner || {};
  const deployment = newLicense.deployment || {};

  const handleDeactivateLicense = async () => {
    try {
      setIsLoading(true)
      const res = await fetch(`${backendUrl}/api/partnership/license/deactivate`, {
        body: JSON.stringify({id: newLicense.id}),
        method: "PATCH",
        credentials: "include",
          headers: {
            Accept: "Application/json",
            "Content-Type": "Application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
      })
      if (!res.ok) {
        let result = await res.json()
        console.log(result.message)
      }else {
        let data = await res.json()
        if (data.success) {
          setLicense(data.license)
          refetch()
        }
      }
    }catch (error) {
      console.log("Error deactivating", (error))
      toast.error(error.message)
    }
    finally {
      setIsLoading(false)
    }
  }


  const getStatusBadge = ({expiry, deployment_id}) => {
    return expiry ? (
      <Badge bg="danger">{deployment_id ? "Used": "Deactivated"}</Badge>
    ) : (
      <Badge bg="success">Active</Badge>
    );
  };

  const getVerificationBadge = (isVerified) => {
    return isVerified ? (
      <Badge bg="success">Verified</Badge>
    ) : (
      <Badge bg="warning">Not Verified</Badge>
    );
  };

  const getDeploymentStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
        return <Badge bg="success">Active</Badge>;
      case "pending":
        return <Badge bg="warning">Pending</Badge>;
      case "suspended":
        return <Badge bg="danger">Suspended</Badge>;
      default:
        return <Badge bg="secondary">{status || "Unknown"}</Badge>;
    }
  };

  const getFeaturesList = (featuresString) => {
    try {
      const features = JSON.parse(featuresString || "[]");
      return features.map((feature, index) => (
        <Badge key={index} bg="info" className="me-2 mb-2">
          {feature.replace("_", " ")}
        </Badge>
      ));
    } catch (e) {
      return <span className="text-muted">No features specified</span>;
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" className="license-details">
      <Modal.Header closeButton className="">
        <Modal.Title>
          <div className="d-flex align-items-center">
            <KeyIcon className="me-2 text-primary" />
            License Details
            <span className="ms-3">{getStatusBadge(newLicense)}</span>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="license-details">
          <Row className="mb-4">
            <Col md={12}>
              <div className="p-3 border rounded">
                <h5 className="border-bottom pb-2 mb-3">
                  <KeyIcon className="me-2 text-primary" />
                  License Information
                </h5>
                <Row>
                  <Col md={6} className="mb-3">
                    <div className="text-muted mb-1">License Key</div>
                    <div className="fw-bold font-monospace">{newLicense.key}</div>
                  </Col>
                  <Col md={6} className="mb-3">
                    {/* <div className="text-muted mb-1">Issued Date</div>
                    <div>
                      {license.issued_date
                        ? formatDate(
                            new Date(license.issued_date),
                            "dd MMM, yyyy"
                          )
                        : "Not specified"}
                    </div> */}
                  </Col>
                  <Col md={3} className="users mb-3">
                    <div className="text-muted mb-1">
                      <AdvancedIcon className="me-1 text-primary" />
                      Advanced Users
                    </div>
                    <div className="fw-bold">{newLicense.advance_users}</div>
                  </Col>
                  <Col md={3} className="users mb-3">
                    <div className="text-muted mb-1">
                      <UserIconModal className="me-1 text-primary" />
                      Core Users
                    </div>
                    <div className="fw-bold">{newLicense.core_users}</div>
                  </Col>
                  <Col md={3} className="users mb-3">
                    <div className="text-muted mb-1">
                      <SelfServiceIcon className="me-1 text-primary" />
                      Self-Service Users
                    </div>
                    <div className="fw-bold">{newLicense.self_service_users}</div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="text-muted mb-1">Created At</div>
                    <div>
                      {formatDate(new Date(newLicense.created_at), "dd MMM, yyyy")}
                      <div className="small text-muted">
                        {formatDate(new Date(newLicense.created_at), "hh:mm a")}
                      </div>
                    </div>
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="text-muted mb-1">Used date</div>
                    {newLicense.issued_date ? 
                    <div>
                      {formatDate(new Date(newLicense.issued_date), "dd MMM, yyyy")}
                      <div className="small text-muted">
                        {formatDate(new Date(newLicense.issued_date), "hh:mm a")}
                      </div>
                    </div>
                    : "N/A"}
                  </Col>
                  <Col md={6} className="mb-3">
                    <div className="text-muted mb-1">Package</div>
                    {newLicense.package ? 
                      <div className="small text-muted">
                        {newLicense.package}
                      </div>
                    : "N/A"}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col md={12}>
              <div className="p-3 border rounded ">
                <h5 className="border-bottom pb-2 mb-3">
                  <SelfServiceIcon className="me-2 text-primary" />
                  Partner Information
                  <span className="ms-2">
                    {getVerificationBadge(partner.is_verified)}
                  </span>
                </h5>
                <Row>
                  {Object.values(partner).length > 0 ? (
                    <>
                      <Col md={6} className="mb-3">
                        <div className="text-muted mb-1">
                          <CompanyIcon className="me-1 text-primary" />
                          Company Name
                        </div>
                        <div className="fw-bold">{partner.company_name}</div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="text-muted mb-1">
                          <EmailIcon className="me-1 text-primary" />
                          Company Email
                        </div>
                        <div>{partner.company_email}</div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="text-muted mb-1">
                          <EmailIcon className="me-1 text-primary" />
                          Personal Email
                        </div>
                        <div>{partner.personal_email}</div>
                      </Col>
                      <Col md={6} className="mb-3">
                        <div className="text-muted mb-1">
                          <LocationIcon className="me-1 text-primary" />
                          Location
                        </div>
                        <div>
                          {partner.city}, {partner.state}, {partner.country}
                        </div>
                      </Col>
                    </>
                  ) : (
                    <>
                      <h5>No partner information yet</h5>
                    </>
                  )}
                </Row>
              </div>
            </Col>
          </Row>

          {deployment && Object.keys(deployment).length > 0 && (
            <Row>
              <Col md={12}>
                <div className="p-3 border rounded ">
                  <h5 className="border-bottom pb-2 mb-3">
                    <ServerIcon className="me-2 text-primary" />
                    Deployment Information
                    <span className="ms-2">
                      {getDeploymentStatusBadge(deployment.status)}
                    </span>
                  </h5>
                  <Row>
                    <Col md={6} className="mb-3">
                      <div className="text-muted mb-1">Company Name</div>
                      <div className="fw-bold">{deployment.company_name}</div>
                    </Col>
                    <Col md={6} className="mb-3">
                      <div className="text-muted mb-1">
                        <Elipses className="me-1 text-primary" />
                        Subdomain
                      </div>
                      <div className="font-monospace">
                        {deployment.subdomain}
                      </div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="text-muted mb-1">Branches</div>
                      <div>{deployment.branches}</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="text-muted mb-1">Commission Rate</div>
                      <div>{deployment.commission_rate}%</div>
                    </Col>
                    <Col md={4} className="mb-3">
                      <div className="text-muted mb-1">Customer ID</div>
                      <div>{deployment.partner_customer_id}</div>
                    </Col>
                    <Col md={12}>
                      <div className="text-muted mb-2">Features</div>
                      <div className="features">
                        {getFeaturesList(deployment.features)}
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {!newLicense.expiry ? (
          <Button className="deactivate" onClick={handleDeactivateLicense}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
                />
              Deactivating...
            </>
          ) : (
          "Deactivate License"
          )}
          </Button>
        ) : !newLicense.deployment_id && (
          <Button className="deactivate" onClick={handleDeactivateLicense}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
                />
              Reactivating...
            </>
          ) : (
          "Reactivate License"
          )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default LicenseDetailsModal;
